import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';

export const TOKEN_STORAGE_KEY = 'sj_token';
// 代行 (インパーソネート) トークンは sessionStorage に保管しタブ独立を実現する。
// 通常 JWT (localStorage の sj_token) は touched せず、代行中だけこちらを優先送信する。
export const IMPERSONATION_TOKEN_KEY = 'sj_imp_token';

// Network Errorリトライ設定
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;
const RETRYABLE_ENDPOINTS = ['/generation/jobs'];

function isNetworkError(error: AxiosError): boolean {
  return !error.response && error.message === 'Network Error';
}

function shouldRetry(error: AxiosError, retryCount: number): boolean {
  if (retryCount >= MAX_RETRIES) return false;
  if (!isNetworkError(error)) return false;

  // 画像生成関連のエンドポイントのみリトライ
  const url = error.config?.url || '';
  return RETRYABLE_ENDPOINTS.some(endpoint => url.includes(endpoint));
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function getStoredToken(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.localStorage.getItem(TOKEN_STORAGE_KEY) || undefined;
}

export function setStoredToken(token: string | undefined) {
  if (typeof window === 'undefined') return;
  if (token) {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    // 新しい元 JWT がセットされた = ログインまたはユーザー切替。前ユーザーが
    // 残した代行トークン (sessionStorage) は別ユーザーのものなので必ず破棄する。
    // これをしないと apiClient が古い代行 token を優先送信し、別ユーザーの
    // セッションを引きずって UI が代行モードに見えてしまう。
    window.sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  } else {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

export function removeStoredToken() {
  setStoredToken(undefined);
}

export function getImpersonationToken(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.sessionStorage.getItem(IMPERSONATION_TOKEN_KEY) || undefined;
}

export function setImpersonationToken(token: string | undefined) {
  if (typeof window === 'undefined') return;
  if (token) {
    window.sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, token);
  } else {
    window.sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  }
}

export function removeImpersonationToken() {
  setImpersonationToken(undefined);
}

// 開発環境では相対パスを使用してViteのプロキシを経由する
// 本番環境ではVITE_BACKEND_ORIGINが設定されている場合のみ絶対URLを使用
const backendOrigin = import.meta.env.VITE_BACKEND_ORIGIN || '';
const isDevelopment = import.meta.env.DEV;
// 開発環境でVITE_BACKEND_ORIGINが設定されていない場合は、相対パスを使用してプロキシを経由
const baseURL = isDevelopment && !backendOrigin ? '/api' : (backendOrigin ? `${backendOrigin}/api` : '/api');

// axios を通さない sendBeacon / fetch(keepalive) から API URL を組み立てるための base。
// apiClient.baseURL と同一。相対 ('/api') の場合は呼び出し側で location.origin を補う。
export const API_BASE_URL = baseURL;

// OAuth 開始系 API (Amazon / Canva / YouTube の認可 URL 取得) 専用の base。
// これらのレスポンスはブラウザ結合 Cookie (oauth_binding_*, SameSite=Lax) を
// アプリと同じホストの first-party Cookie として置く必要があるため、
// VITE_BACKEND_ORIGIN (run.app) への直接 XHR ではなく、必ず同一オリジンの
// /api (本番=Vercel rewrite / dev=vite proxy) を経由させる。
// クロスサイト XHR では Lax の Set-Cookie が保存されず、OAuth コールバックの
// state 照合が必ず「Invalid state parameter」で失敗する。
export const OAUTH_BINDING_API_BASE = '/api';

export const apiClient = axios.create({
  baseURL,
  timeout: 360000, // 6分: gemini-3-pro-image-previewの高解像度生成（最大5分）+ バッファ
  withCredentials: true,
});

// フロントエンドのビルド識別子 (vite.config.ts の define)。テスト環境など未定義なら 'dev'。
export const APP_BUILD_ID: string =
  typeof __APP_BUILD_ID__ !== 'undefined' && __APP_BUILD_ID__ ? __APP_BUILD_ID__ : 'dev';

/** 全リクエストに付けるビルド識別子ヘッダ。バックエンドは「無し = 旧ビルド」として互換応答を出し分ける
 *  (docs/features/async-jobs-bloat-cleanup-plan.md A-4)。 */
export const CLIENT_BUILD_HEADER = 'X-Client-Build';

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.headers = config.headers || {};
  config.headers[CLIENT_BUILD_HEADER] = APP_BUILD_ID;
  // 代行トークン (sessionStorage) があれば優先。タブ独立で動作するため。
  const token = getImpersonationToken() ?? getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const handleResponse = (response: AxiosResponse) => response;
const handleError = async (error: AxiosError): Promise<AxiosResponse> => {
  const config = error.config as InternalAxiosRequestConfig & {
    _retryCount?: number;
    _impRetried?: boolean;
  };
  const retryCount = config?._retryCount || 0;

  // Network Errorでリトライ対象の場合
  if (shouldRetry(error, retryCount)) {
    config._retryCount = retryCount + 1;
    console.warn(`[apiClient] Network Error detected, retrying... (attempt ${config._retryCount}/${MAX_RETRIES})`);

    await delay(RETRY_DELAY_MS * config._retryCount); // Exponential backoff

    // リトライ実行
    return apiClient.request(config);
  }

  const status = error.response?.status;
  const responseData = error.response?.data as any;

  // 代行 JWT が失効しているシグナル (X-Impersonation-Expired ヘッダ) を検知。
  // sessionStorage の代行トークンを破棄し、元 JWT (localStorage) で 1 回だけリトライ。
  const impersonationExpired =
    (error.response?.headers?.['x-impersonation-expired'] === 'true') &&
    (status === 401 || status === 403);
  if (impersonationExpired && !config._impRetried && getImpersonationToken()) {
    removeImpersonationToken();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sj:impersonation-expired'));
    }
    config._impRetried = true;
    return apiClient.request(config);
  }

  // パートナーコンテキスト不一致 (別 partner のドメインに別 partner の JWT でアクセス):
  // 現ドメインで再ログインすれば正しい partner の組織に解決されるため、トークンを破棄して
  // ログイン画面へ誘導する (401 と同じ扱い)。バックエンドの guard_partner_context_match と対。
  if (status === 403 && responseData?.code === 'PARTNER_CONTEXT_MISMATCH') {
    removeStoredToken();
    removeImpersonationToken();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sj:token', { detail: null }));
    }
    console.error('API error (partner context mismatch):', error);
    return Promise.reject(error);
  }

  // Amazon認証エラー（actionフィールドがある）の場合は、ユーザーのログイントークンを削除しない
  const isAmazonAuthError = responseData?.action === 'reauthenticate' || responseData?.action === 'contact_admin';

  // Canva認証が必要なエラー（need_authフィールドがある）の場合も、ユーザーのログイントークンを削除しない
  const isCanvaAuthRequired = responseData?.need_auth === true;

  // 401 のみトークンを削除する (認証自体が失効)。403 は「このリソースへの権限がない」
  // 意味で、トークンは有効なので保持する。以前は 403 でも削除していたが、一般ユーザーが
  // /admin/* にアクセスしただけでセッションが切れる副作用があったため修正。
  if (status === 401 && !isAmazonAuthError && !isCanvaAuthRequired) {
    removeStoredToken();
    // 元 JWT も失効した場合は代行トークンも同時に破棄 (孤立を防ぐ)
    removeImpersonationToken();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sj:token', { detail: null }));
    }
  }
  console.error('API error:', error);
  return Promise.reject(error);
};

apiClient.interceptors.response.use(handleResponse, handleError);
